import { Asset } from '@api/models'
import { KIA_APP_COMPANY_ID, KIA_ASSET_COMPANY_ID } from '@constants'

/**
 * Coerces a claim/stored value into an AppCompanyID. Anything that is not a
 * usable org id (missing, empty, non-numeric) comes back undefined.
 */
export function parseAppCompanyID(value: unknown): number | undefined {
  const appCompanyID = Number(value)
  return Number.isFinite(appCompanyID) && appCompanyID > 0 ? appCompanyID : undefined
}

/**
 * Reads the `organization_id` claim off the persisted OIDC session. This is the
 * claim the API turns into AppCompanyID (`WorthITController.cs`), so it is the
 * same org the report endpoints scope their queries to.
 */
function getOidcAppCompanyID(): number | undefined {
  try {
    const oidcUser = JSON.parse(
      sessionStorage.getItem(
        `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`
      )
    )
    return parseAppCompanyID(oidcUser?.profile?.organization_id)
  } catch {
    return undefined
  }
}

/**
 * Reads the current organization's AppCompanyID from the persisted user.
 * `store/actions/user.ts` writes `appCompanyID` onto the `currentUser`
 * localStorage entry at login. Returns undefined if unavailable.
 */
function getStoredAppCompanyID(): number | undefined {
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'))
    return parseAppCompanyID(currentUser?.appCompanyID)
  } catch {
    return undefined
  }
}

/**
 * The current organization's AppCompanyID, or undefined when it cannot be read.
 */
function getCurrentAppCompanyID(): number | undefined {
  return getOidcAppCompanyID() ?? getStoredAppCompanyID()
}

/**
 * True when the asset belongs to the Kia asset company (200) and the current
 * org is the Kia org (AppCompanyID 13). If the org id is not available in
 * localStorage, falls back to the asset company check alone.
 */
export function isKiaCompany(asset: Asset): boolean {
  if (asset?.AssetCompanyID !== KIA_ASSET_COMPANY_ID) {
    return false
  }

  const appCompanyID = getCurrentAppCompanyID()

  if (appCompanyID === undefined || appCompanyID === null) {
    return true
  }

  return appCompanyID === KIA_APP_COMPANY_ID
}

/**
 * True when the given org - by default the signed-in one - is the Kia org.
 * Unlike isKiaCompany there is no asset to fall back on, so an org id that
 * cannot be read reads as "not Kia" and Kia-only features stay hidden.
 */
export function isKiaOrg(appCompanyID?: number): boolean {
  const id = appCompanyID ?? getCurrentAppCompanyID()
  return id === KIA_APP_COMPANY_ID
}
